import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, RotateCcw } from 'lucide-react';
import { ValidatorComment, ValidatorSeverity } from '../../services/taskValidator';
import { useReviewContext } from './ReviewContext';

interface VerificationIssue {
  comment: ValidatorComment;
  fieldPath: string;
  originalIndex: number;
  resolved: boolean;
}

interface VerificationColumnProps {
  validatorStatus?: 'idle' | 'running' | 'done' | 'error';
  validatorError?: string | null;
  submissionValidation?: string | null;
}

/**
 * The transient validator result shown in the same rail as reviewer
 * comments. The field itself stays responsible for the highlight and scroll;
 * this list only owns issue lifecycle and navigation into the task.
 */
export function VerificationColumn({
  validatorStatus = 'idle',
  validatorError = null,
  submissionValidation = null,
}: VerificationColumnProps) {
  const ctx = useReviewContext();
  const [tab, setTab] = useState<'unresolved' | 'resolved'>('unresolved');

  const issues = useMemo(() => {
    const next: VerificationIssue[] = [];
    ctx.validatorCommentsByField.forEach((comments, fieldPath) => {
      comments.forEach((comment, originalIndex) => {
        const key = `${fieldPath}::${originalIndex}`;
        next.push({
          comment,
          fieldPath,
          originalIndex,
          resolved: ctx.dismissedValidatorIds.has(key),
        });
      });
    });
    return next;
  }, [ctx.validatorCommentsByField, ctx.dismissedValidatorIds]);

  const unresolved = useMemo(() => issues.filter((issue) => !issue.resolved), [issues]);
  const resolved = useMemo(() => issues.filter((issue) => issue.resolved), [issues]);
  const visible = tab === 'resolved' ? resolved : unresolved;

  return (
    <div className="flex h-full flex-col">
      {submissionValidation && (
        <div
          role="alert"
          className="border-b border-amber-200 bg-amber-50 px-4 py-3"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
            Fix before submitting
          </p>
          <p className="mt-1 text-sm text-amber-900">{submissionValidation}</p>
        </div>
      )}
      <div role="tablist" aria-label="Validation issue status" className="flex flex-shrink-0 border-b border-gray-200 px-3">
        <TabButton
          active={tab === 'unresolved'}
          count={unresolved.length}
          onClick={() => setTab('unresolved')}
        >
          Unresolved
        </TabButton>
        <TabButton
          active={tab === 'resolved'}
          count={resolved.length}
          onClick={() => setTab('resolved')}
        >
          Resolved
        </TabButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {visible.length === 0 ? (
          <div className="px-2 py-8 text-center">
            {validatorStatus === 'error' && tab === 'unresolved' ? (
              <AlertTriangle className="mx-auto h-6 w-6 text-red-500" />
            ) : (
              <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
            )}
            <p className="mt-2 text-sm font-medium text-gray-700">
              {tab === 'resolved'
                ? 'No resolved issues.'
                : validatorStatus === 'done'
                  ? 'No validation issues found.'
                  : validatorStatus === 'error'
                    ? 'Verification could not be completed.'
                    : 'No verification issues.'}
            </p>
            {tab === 'unresolved' && validatorStatus === 'error' && validatorError && (
              <p className="mt-1 break-words text-xs text-red-700">{validatorError}</p>
            )}
            {tab === 'unresolved' && validatorStatus === 'idle' && issues.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Run validation to check this task.
              </p>
            )}
          </div>
        ) : (
          <ul className="space-y-2">
            {visible.map((issue) => (
              <VerificationIssueCard
                key={`${issue.fieldPath}::${issue.originalIndex}`}
                issue={issue}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        '-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-gray-500 hover:text-gray-700',
      ].join(' ')}
    >
      {children}
      <span className={`ml-1 ${active ? 'text-primary' : 'text-gray-400'}`}>
        ({count})
      </span>
    </button>
  );
}

function VerificationIssueCard({ issue }: { issue: VerificationIssue }) {
  const ctx = useReviewContext();
  const styles = severityStyles(issue.comment.severity);
  const Icon = styles.Icon;

  return (
    <li>
      <article
        className={[
          'rounded-md border bg-white transition-colors',
          issue.resolved
            ? 'border-emerald-200'
            : 'border-amber-300 hover:border-amber-400',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={() => ctx.navigateToField(issue.fieldPath)}
          aria-label={`Open verification issue on ${issue.fieldPath}`}
          className="block w-full px-3 py-2.5 text-left hover:bg-gray-50"
        >
          <div className="flex items-start gap-2">
            <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${styles.icon}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-semibold ${styles.label}`}>{styles.title}</span>
                {issue.resolved && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Resolved
                  </span>
                )}
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                {issue.comment.text}
              </p>
              <p className="mt-2 truncate font-mono text-[10px] text-gray-500" title={issue.fieldPath}>
                {issue.fieldPath}
              </p>
            </div>
          </div>
        </button>

        <div className="flex justify-end border-t border-gray-100 px-3 py-2">
          {issue.resolved ? (
            <button
              type="button"
              onClick={() => ctx.restoreValidatorComment(issue.fieldPath, issue.originalIndex)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <RotateCcw className="h-3 w-3" />
              Reopen
            </button>
          ) : (
            <button
              type="button"
              onClick={() => ctx.dismissValidatorComment(issue.fieldPath, issue.originalIndex)}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Mark as resolved
            </button>
          )}
        </div>
      </article>
    </li>
  );
}

function severityStyles(severity: ValidatorSeverity) {
  if (severity === 'warning') {
    return {
      Icon: AlertTriangle,
      title: 'Needs attention',
      icon: 'text-amber-600',
      label: 'text-amber-900',
    };
  }
  return {
    Icon: Info,
    title: 'Suggestion',
    icon: 'text-sky-600',
    label: 'text-sky-900',
  };
}
